import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, Copy, Download, ExternalLink, XCircle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "../../../i18n";
import { api } from "../../../lib/api";
import { fmtDateTime, fmtMoney } from "../../../lib/format";
import { STATUS_CLS, payErr } from "../../../lib/payments";
import { Panel, btnGhost, btnPrimary } from "../../../components/common/Page";
import { SkeletonBlock } from "../../../components/common/EmptyState";

// Polls our backend; the status only changes when the provider webhook (or SoftOne read-back) confirms it.
const usePayment = (id) => {
  const [p, setP] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = () => api.get(`/payments/intents/${id}`).then((r) => { if (!stop) setP(r.data); }).catch(() => {});
    load();
    const t = setInterval(load, 2500);
    return () => { stop = true; clearInterval(t); };
  }, [id]);
  return [p, setP];
};

const openReceipt = async (id) => {
  const r = await api.get(`/payments/intents/${id}/receipt.pdf`, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank", "noopener");
};

const Summary = ({ p }) => {
  const { t } = useI18n();
  const rows = [[t("x.pay.receiptNo"), p.receipt_no], [t("x.pay.method"), t(`x.pay.${p.method}`)], [t("x.pay.total"), fmtMoney(p.total)],
    [t("x.pay.softoneNo"), p.softone_receipt_no], [t("x.pay.paidAt"), p.paid_at && fmtDateTime(p.paid_at)]].filter(([, v]) => v);
  return <dl className="mt-4 grid gap-2 text-sm">{rows.map(([k, v]) => <div key={k + v} className="flex justify-between gap-3"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-navy">{v}</dd></div>)}</dl>;
};

export function PayStatus() {
  const { t } = useI18n();
  const { id } = useParams();
  const [qs] = useSearchParams();
  const [p] = usePayment(id);
  if (!p) return <SkeletonBlock className="h-48" />;
  const cancelled = qs.get("cancelled") && p.status === "pending";
  const Icon = p.status === "paid" || p.status === "refunded" ? CheckCircle2 : p.status === "pending" && !cancelled ? Clock : XCircle;
  const title = p.status === "paid" || p.status === "refunded" ? t("x.pay.paidTitle") : cancelled ? t("x.pay.cancelled") : p.status === "pending" ? t("x.pay.waiting") : t("x.pay.failedTitle");
  return (
    <div className="mx-auto max-w-lg space-y-4" data-testid="pay-status-page">
      <Panel>
        <div className="flex items-start gap-3">
          <Icon className={`h-9 w-9 shrink-0 ${p.status === "paid" ? "text-brand" : p.status === "pending" && !cancelled ? "text-amber-500" : "text-rose-500"}`} />
          <div><p className="text-lg font-bold text-navy" data-testid="pay-status-title">{title}</p>
            <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${STATUS_CLS[p.status]}`} data-testid="pay-status-badge">{t(`x.pay.status.${p.status}`)}</span></div>
        </div>
        {p.status === "pending" && !cancelled && <p className="mt-3 text-xs text-slate-500">{t("x.pay.waitingNote")}</p>}
        <Summary p={p} />
        <div className="mt-5 flex flex-wrap gap-2">
          {(p.status === "paid" || p.status === "refunded") && <button type="button" onClick={() => openReceipt(p.id).catch(() => toast.error(t("common.error")))} className={btnPrimary} data-testid="pay-receipt-button"><Download className="h-4 w-4" />{t("x.pay.receipt")}</button>}
          {(cancelled || ["failed", "expired"].includes(p.status)) && <Link to="/app/pay" className={btnPrimary} data-testid="pay-retry">{t("x.pay.retry")}</Link>}
          <Link to="/app/pay" className={btnGhost} data-testid="pay-status-back">{t("x.pay.history")}</Link>
        </div>
      </Panel>
    </div>
  );
}

const IrisQr = ({ id }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => { api.get(`/payments/intents/${id}/qr.png`, { responseType: "blob" }).then((r) => setUrl(URL.createObjectURL(r.data))).catch(() => {}); }, [id]);
  return url ? <img src={url} alt="IRIS QR" className="mx-auto h-56 w-56 rounded-xl ring-1 ring-slate-200" data-testid="iris-qr" /> : <SkeletonBlock className="mx-auto h-56 w-56" />;
};

// IRIS (QR + bank-app redirect) and the SoftOne payment page; sandbox buttons appear only for mock providers.
const CopyRow = ({ label, value, testId }) => {
  const { t } = useI18n();
  return (
    <div className="flex items-center gap-2 border-b border-slate-100 py-3 last:border-0">
      <div className="min-w-0 flex-1"><p className="text-xs text-slate-500">{label}</p><p className="break-all font-semibold tabular-nums text-navy" data-testid={testId}>{value}</p></div>
      <button type="button" onClick={() => navigator.clipboard?.writeText(value).then(() => toast.success(t("x.pay.copied")))} aria-label={t("x.pay.copy")} className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-canvas text-navy" data-testid={`${testId}-copy`}><Copy className="h-4 w-4" /></button>
    </div>
  );
};


export function PayProvider({ kind }) {
  const { t } = useI18n();
  const { id } = useParams();
  const [p] = usePayment(id);
  if (!p) return <SkeletonBlock className="h-64" />;
  if (p.status !== "pending") return <PayStatus />;
  return (
    <div className="mx-auto max-w-lg space-y-4" data-testid={`pay-${kind}-page`}>
      <Panel title={t(kind === "iris" ? "x.pay.irisTitle" : "x.pay.softoneTitle")}>
        <p className="text-3xl font-bold tabular-nums text-navy" data-testid="pay-provider-total">{fmtMoney(p.total)}</p>
        <p className="mt-1 text-xs text-slate-500">{p.customer_name} · {p.provider_ref}</p>
        {kind === "iris" ? <div className="mt-4 space-y-3">
          {p.bank_url ? <>
            <p className="text-sm text-slate-600">{t("x.pay.payhubNote")}</p>
            <CopyRow label={t("x.pay.bankRef")} value={p.provider_ref} testId="iris-rf" />
            <a href={p.bank_url} target="_blank" rel="noopener noreferrer" className={`${btnPrimary} w-full`} data-testid="iris-open-bank"><ExternalLink className="h-4 w-4" />{t("x.pay.payWithIris")}</a>
          </> : <><p className="text-sm text-slate-600">{t("x.pay.irisScan")}</p><IrisQr id={id} /></>}</div>
          : <p className="mt-4 text-sm text-slate-600">{t("x.pay.softoneNote")}</p>}
        <p className="mt-4 text-xs text-slate-500">{t("x.pay.waitingNote")}</p>
      </Panel>
    </div>
  );
}
